import React from "react";
import { Card, Col, Row, Tooltip } from "antd";
import {
  AimOutlined,
  CheckCircleOutlined,
  FileDoneOutlined,
  HourglassOutlined,
  PercentageOutlined,
} from "@ant-design/icons";
import {
  ExecutionMonthColumn,
  ExecutionSummaryData,
  fmtINR,
  fmtINRCompact,
  fmtINRSigned,
  fmtMonth,
  num,
} from "./executionShared";

// KPI cards for the Monthly Execution Summary (style cloned from PnlKpiCards):
// Planned Revenue / Planned Expenditure / Executed Expenditure / Closing Backlog / Execution %.

const valueStyle: React.CSSProperties = {
  fontSize: 20,
  fontWeight: 700,
  color: "#1a1a2e",
  marginTop: 6,
  fontVariantNumeric: "tabular-nums",
  cursor: "default",
  width: "fit-content",
};

const titleStyle = (color: string): React.CSSProperties => ({
  fontSize: 12.5,
  fontWeight: 600,
  color,
  display: "flex",
  alignItems: "center",
  gap: 6,
});

const cardStyle = (background: string, border: string): React.CSSProperties => ({
  borderRadius: 12,
  background,
  border: `1px solid ${border}`,
  height: "100%",
});

const subStyle: React.CSSProperties = {
  fontSize: 12,
  color: "#8c8c8c",
  marginTop: 2,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const ExecutionKpiCards: React.FC<{
  data?: ExecutionSummaryData | null;
}> = ({ data }) => {
  const months: ExecutionMonthColumn[] = data?.months || [];
  const totals = data?.totals;
  const clientPlan = num(totals?.clientPlanAmount);
  const target = num(totals?.targetAmount);
  const executed = num(totals?.executedAmount);
  const closingBacklog = num(totals?.closingBacklog);
  const executionPct = target > 0 ? Number(((executed / target) * 100).toFixed(2)) : 0;
  const behindPlan = closingBacklog > 0;

  const actualizedMonths = months.filter((m) => m.isActualized);
  const lastActual = data?.scheduleRange?.lastActualMonth;

  return (
    <Row gutter={[12, 12]} justify="start" style={{ marginBottom: 16 }}>
      <Col flex="0 1 224px" style={{ minWidth: 186 }}>
        <Card size="small" style={cardStyle("#f9f4ff", "#d3adf7")} bodyStyle={{ padding: "12px 16px" }}>
          <div style={titleStyle("#722ed1")}>
            <FileDoneOutlined /> Planned Revenue
          </div>
          <Tooltip title={fmtINR(clientPlan)}>
            <div style={valueStyle}>{fmtINRCompact(clientPlan)}</div>
          </Tooltip>
          <div style={subStyle}>Client billing program (sched qty × Basic Rate)</div>
        </Card>
      </Col>
      <Col flex="0 1 224px" style={{ minWidth: 186 }}>
        <Card size="small" style={cardStyle("#f0f7ff", "#bae0ff")} bodyStyle={{ padding: "12px 16px" }}>
          <div style={titleStyle("#1677ff")}>
            <AimOutlined /> Planned Expenditure
          </div>
          <Tooltip title={fmtINR(target)}>
            <div style={valueStyle}>{fmtINRCompact(target)}</div>
          </Tooltip>
          <div style={subStyle}>Planned execution cost (3M + Equipment)</div>
        </Card>
      </Col>
      <Col flex="0 1 224px" style={{ minWidth: 186 }}>
        <Card size="small" style={cardStyle("#f6ffed", "#b7eb8f")} bodyStyle={{ padding: "12px 16px" }}>
          <div style={titleStyle("#389e0d")}>
            <CheckCircleOutlined /> Executed Expenditure
          </div>
          <Tooltip title={fmtINR(executed)}>
            <div style={valueStyle}>{fmtINRCompact(executed)}</div>
          </Tooltip>
          <div style={subStyle}>
            {actualizedMonths.length
              ? `Actuals through ${lastActual ? fmtMonth(lastActual) : "—"}`
              : "No actuals captured yet"}
          </div>
        </Card>
      </Col>
      <Col flex="0 1 224px" style={{ minWidth: 186 }}>
        <Card
          size="small"
          style={cardStyle(behindPlan ? "#fff7e6" : "#f6ffed", behindPlan ? "#ffd591" : "#b7eb8f")}
          bodyStyle={{ padding: "12px 16px" }}
        >
          <div style={titleStyle(behindPlan ? "#fa8c16" : "#389e0d")}>
            <HourglassOutlined /> Closing Backlog
          </div>
          <Tooltip title={fmtINRSigned(closingBacklog, true)}>
            <div style={{ ...valueStyle, color: behindPlan ? "#b46a12" : "#237804" }}>
              {fmtINRSigned(closingBacklog)}
            </div>
          </Tooltip>
          <div style={subStyle}>
            {behindPlan
              ? "Work value carried into the next month"
              : closingBacklog < 0
              ? "Ahead of plan"
              : "On plan"}
          </div>
        </Card>
      </Col>
      <Col flex="0 1 224px" style={{ minWidth: 186 }}>
        <Card size="small" style={cardStyle("#fafafa", "#e8e8e8")} bodyStyle={{ padding: "12px 16px" }}>
          <div style={titleStyle("#526484")}>
            <PercentageOutlined /> Execution
          </div>
          <div style={{ ...valueStyle, color: executionPct >= 100 ? "#237804" : "#022c57" }}>
            {target > 0 ? `${executionPct}%` : "—"}
          </div>
          <div style={subStyle}>Executed ÷ planned expenditure (visible window)</div>
        </Card>
      </Col>
    </Row>
  );
};

export default ExecutionKpiCards;
